import React, { useState, useEffect } from "react";
import {
  FaInstagram,
  FaYoutube,
  FaTiktok,
  FaTwitter,
  FaGlobe,
  FaPhone,
  FaMapMarkerAlt,
  FaUser,
  FaCalendar,
  FaInfoCircle,
  FaEdit,
} from "react-icons/fa";
import { ImFacebook2 } from "react-icons/im";
import { useParams } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from "../../theme.ts";
import ProfileEdit from "../../components/ProfileEdit.tsx";
import { useUserProfile, useVerifyOrganization } from "../../hooks/useUsers.ts";
import { useChangePassword } from "../../hooks/useAuth.ts";
import ChangePasswordModal from "../../components/common/ChangePasswordModal.tsx";
import toast from "react-hot-toast";

const InfluencerProfile: React.FC = () => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<any>(null);
  const [error, setError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const { id } = useParams();

  const rawData = localStorage.getItem("user");
  const cred = rawData ? JSON.parse(rawData) : null;
  const [user, setUser] = useState<any>();
  const profileId = id || cred?.user?.id;
  const isOwnProfile = !id || id === cred?.user?.id;

  const { data: apiProfileData, isLoading: isHookLoading, error: hookError } = useUserProfile(profileId);
  const verifyOrganizationMutation = useVerifyOrganization();
  const changePasswordMutation = useChangePassword();

  // Enhanced profile loading with data merging
  const loadUserProfile = async () => {
    // Logic moved to effect dependent on hook data
  };

  useEffect(() => {
    if (apiProfileData) {
      // Start with profile data from login response (only if own profile ideally, but logic was generic)
      let localProfileData = cred?.user?.profile;
      const apiData = (apiProfileData as any)?.user?.profile || apiProfileData;

      // Merge logic
      if (apiData && typeof apiData === 'object') {
        const mergedData = { ...localProfileData };
        Object.keys(apiData).forEach(key => {
          if (apiData[key] !== null && apiData[key] !== undefined && apiData[key] !== '') {
            mergedData[key] = apiData[key];
          }
        });
        localProfileData = mergedData;
      }

      setProfile(localProfileData || apiData); // Fallback to apiData if local is null
      setUser((apiProfileData as any)?.user || cred?.user);
      setLoading(false);
    } else if (hookError) {
      // Fallback to local
      setProfile(cred?.user?.profile);
      setUser(cred?.user);
      setError(true);
      setLoading(false);
    } else if (isHookLoading) {
      setLoading(true);
    }
  }, [apiProfileData, isHookLoading, hookError, cred]);

  const handleVerification = async () => {
    try {
      const loadingToast = toast.loading("Submitting verification request...");

      await verifyOrganizationMutation.mutateAsync();

      toast.dismiss(loadingToast);
      // Success handled by hook or here double check
    } catch (err) {
      console.error("Verification error:", err);
      // Error handled by hook
    }
  };

  const handleChangePasswordWrapper = async (password: string, confirm: string) => {
    return changePasswordMutation.mutateAsync({ old_password: "", new_password: password, confirm_password: confirm });
    // Wait, ChangePasswordModal usually asks for Old Password?
    // Let's check the Modal. If it passes 3 args or 2 args. 
    // Standard is usually old, new, confirm. 
    // The modal content is not visible here but `changeUserPassword` in `Users.ts` signature might be relevant.
    // Assuming the modal handles the inputs and calls this function. 
    // `Users.ts` `changeUserPassword(old, new, confirm)`.
    // If the Modal passes 3 args:
  };

  const handleEditClick = () => {
    toast.success("Opening edit mode...");
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    // Refresh profile data after edit handled by RQ invalidation theoretically if we used mutations for edit.
    // Ideally we should invalidate 'userProfile' query here if edit was successful.
  };

  const renderPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case "instagram":
        return <FaInstagram className="text-pink-600" />;
      case "youtube":
        return <FaYoutube className="text-red-600" />;
      case "tiktok":
        return <FaTiktok className="text-black" />;
      case "twitter":
      case "x":
        return <FaTwitter className="text-blue-400" />;
      case "facebook":
        return <ImFacebook2 className="text-blue-700" />;
      default:
        return <FaGlobe className="text-gray-500" />;
    }
  };

  const getProfileLinks = (linksObj: any) => {
    if (!linksObj || typeof linksObj !== "object") return [];
    return Object.entries(linksObj)
      .map(([platform, data]: [string, any]) => {
        if (data?.link) {
          return { platform, url: data.link };
        }
        return null;
      })
      .filter(Boolean) as { platform: string; url: string }[];
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: `linear-gradient(to bottom right, ${currentTheme.background}, ${currentTheme.surface})`,
        padding: "1.5rem",
      }}
      className="md:p-6"
    >
      {isEditing ? (
        <ProfileEdit onCancel={handleEditCancel} />
      ) : loading ? (
        <div className="flex justify-center items-center mt-6">
          <div className="loader border-t-4 border-primary-600 rounded-full w-12 h-12 animate-spin"></div>
          <p className="ml-4 text-primary-700">Loading...</p>
        </div>
      ) : error || !profile ? (
        <div className="flex-1 flex items-center justify-center">
          <div
            className="p-8 rounded-2xl shadow-xl text-center"
            style={{
              backgroundColor: currentTheme.surface,
              border: `1px solid ${currentTheme.border}`,
            }}
          >
            <h2
              className="text-2xl font-bold mb-2"
              style={{ color: currentTheme.primary }}
            >
              Profile Not Found
            </h2>
            <p style={{ color: currentTheme.textSecondary }}>
              The influencer profile you are looking for does not exist.
            </p>
            <button
              onClick={() => window.location.reload()} // Simple reload to refetch
              className="mt-4 px-4 py-2 rounded-lg transition-colors"
              style={{
                backgroundColor: currentTheme.primary,
                color: currentTheme.surface,
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Profile Header */}
          <div
            className="rounded-2xl shadow-xl p-8 flex flex-col md:flex-row items-center gap-6 transition-all duration-300 hover:shadow-2xl"
            style={{
              backgroundColor: currentTheme.surface,
              border: `1px solid ${currentTheme.border}`,
            }}
          >
            <div className="relative">
              <div
                className="w-40 h-40 rounded-full overflow-hidden shadow-lg ring-4 ring-offset-4"
                style={{
                  borderColor: currentTheme.primary,
                }}
              >
                <img
                  src={
                    profile?.profile_picture
                      ? `${process.env.REACT_APP_PF}${profile.profile_picture}`
                      : "/default-avatar.jpg"
                  }
                  alt={profile.first_name || "Influencer"}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/default-avatar.jpg";
                  }}
                />
              </div>
            </div>

            <div className="text-center md:text-left flex-1">
              <div className="flex items-center justify-center md:justify-start gap-4 mb-3">
                <h1
                  className="text-3xl font-bold"
                  style={{ color: currentTheme.primary }}
                >
                  {profile.first_name || "Influencer"} {profile.last_name || ""}
                </h1>
                {isOwnProfile && (
                  <button
                    onClick={handleEditClick}
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                    style={{ color: currentTheme.primary }}
                    title="Edit Profile"
                  >
                    <FaEdit size={20} />
                  </button>
                )}
              </div>

              <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                <FaMapMarkerAlt style={{ color: currentTheme.primary }} />
                <span style={{ color: currentTheme.textSecondary }}>
                  {profile.district || "Unknown District"},{" "}
                  {profile.state || "Unknown State"}
                </span>
              </div>

              <p className="text-sm text-primary-500 mt-1">
                Member since {profile?.created_at ? new Date(profile.created_at).getFullYear() : "2024"}
              </p>
            </div>
          </div>

          {/* Grid Sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Personal Info */}
            <div
              className="rounded-2xl shadow-lg p-6 transition-all duration-300 hover:shadow-2xl"
              style={{
                backgroundColor: currentTheme.surface,
                border: `1px solid ${currentTheme.border}`,
              }}
            >
              <h3
                className="text-xl font-semibold mb-4 flex items-center gap-2 border-b pb-2"
                style={{
                  color: currentTheme.primary,
                  borderColor: currentTheme.border,
                }}
              >
                <FaUser /> Personal Info
              </h3>
              <div className="space-y-3">
                <p style={{ color: currentTheme.text }}>
                  <strong>First Name:</strong>{" "}
                  {profile.first_name || "Not provided"}
                </p>
                <p style={{ color: currentTheme.text }}>
                  <strong>Last Name:</strong>{" "}
                  {profile.last_name || "Not provided"}
                </p>
                <p style={{ color: currentTheme.text }}>
                  <strong>Date of Birth:</strong>{" "}
                  {profile.date_of_birth || "Not provided"}
                </p>
                <p style={{ color: currentTheme.text }}>
                  <strong>Profile ID:</strong>{" "}
                  {profile.profile_id || "Not provided"}
                </p>
              </div>
            </div>

            {/* Contact */}
            <div
              className="rounded-2xl shadow-lg p-6 transition-all duration-300 hover:shadow-2xl"
              style={{
                backgroundColor: currentTheme.surface,
                border: `1px solid ${currentTheme.border}`,
              }}
            >
              <h3
                className="text-xl font-semibold mb-4 flex items-center gap-2 border-b pb-2"
                style={{
                  color: currentTheme.primary,
                  borderColor: currentTheme.border,
                }}
              >
                <FaPhone style={{ transform: 'rotate(180deg)' }} /> Contact Information
              </h3>
              <div className="space-y-3">
                <p style={{ color: currentTheme.text }}>
                  <strong>Phone:</strong>{" "}
                  {profile.phone_number || "Not provided"}
                </p>
                <p style={{ color: currentTheme.text }}>
                  <strong>Email:</strong> {user?.email || "Not provided"}
                </p>
                <p style={{ color: currentTheme.text }}>
                  <strong>Address:</strong>{" "}
                  {profile.district && profile.state
                    ? `${profile.district}, ${profile.state}${profile.pincode ? ` - ${profile.pincode}` : ""}`
                    : "Not provided"}
                </p>
              </div>
            </div>

            {/* Social */}
            <div
              className="rounded-2xl shadow-lg p-6 transition-all duration-300 hover:shadow-2xl"
              style={{
                backgroundColor: currentTheme.surface,
                border: `1px solid ${currentTheme.border}`,
              }}
            >
              <h3
                className="text-xl font-semibold mb-4 flex items-center gap-2 border-b pb-2"
                style={{
                  color: currentTheme.primary,
                  borderColor: currentTheme.border,
                }}
              >
                <FaGlobe /> Social Links
              </h3>
              <div className="flex flex-wrap gap-3 mt-4">
                {getProfileLinks(profile.profile_links).length > 0 ? (
                  getProfileLinks(profile.profile_links).map((link, idx) => (
                    <a
                      key={idx}
                      href={
                        link.url.startsWith("http")
                          ? link.url
                          : `https://${link.url}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 rounded-full hover:scale-110 transition-all duration-300 shadow"
                      style={{
                        backgroundColor: `${currentTheme.primary}10`,
                        color: currentTheme.primary,
                      }}
                    >
                      {renderPlatformIcon(link.platform)}
                    </a>
                  ))
                ) : (
                  <p
                    className="text-sm"
                    style={{ color: currentTheme.textSecondary }}
                  >
                    No social links
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div
            className="rounded-2xl shadow-lg p-6 transition-all duration-300 hover:shadow-2xl"
            style={{
              backgroundColor: currentTheme.surface,
              border: `1px solid ${currentTheme.border}`,
            }}
          >
            <h3
              className="text-xl font-semibold mb-4 flex items-center gap-2 border-b pb-2"
              style={{
                color: currentTheme.primary,
                borderColor: currentTheme.border,
              }}
            >
              <FaInfoCircle /> Additional Information
            </h3>
            <div className="space-y-4">
              <p style={{ color: currentTheme.text }}>
                <strong>Interests:</strong>{" "}
                {(() => {
                  if (!profile?.interests) {
                    return "No interests specified";
                  }

                  let interestsArray: string[] = [];

                  // Handle array case
                  if (Array.isArray(profile.interests)) {
                    interestsArray = profile.interests.flatMap((item: unknown) => {
                      if (typeof item === 'string') {
                        const cleaned = item.replace(/^\["?|"?\]?$|"/g, '').trim();
                        return cleaned ? [cleaned] : [];
                      }
                      return [];
                    });
                  }
                  // Handle string case
                  else if (typeof profile.interests === 'string') {
                    try {
                      const parsed = JSON.parse(profile.interests);
                      if (Array.isArray(parsed)) {
                        interestsArray = parsed.filter((item: any) => item && typeof item === 'string');
                      }
                    } catch {
                      interestsArray = profile.interests.split(',').map((i: string) => i.trim()).filter((i: string) => i);
                    }
                  }

                  const uniqueInterests = Array.from(new Set(interestsArray.filter(item => item && item.trim())));
                  return uniqueInterests.length > 0 ? uniqueInterests.join(", ") : "No interests specified";
                })()}
              </p>
            </div>
          </div>

          {/* Account Information */}
          <div
            className="rounded-2xl shadow-lg p-6 transition-all duration-300 hover:shadow-2xl"
            style={{
              backgroundColor: currentTheme.surface,
              border: `1px solid ${currentTheme.border}`,
            }}
          >
            <h3
              className="text-xl font-semibold mb-4 flex items-center gap-2 border-b pb-2"
              style={{
                color: currentTheme.primary,
                borderColor: currentTheme.border,
              }}
            >
              <FaCalendar /> Account Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <p style={{ color: currentTheme.text }}>
                <strong>Email:</strong> {user?.email || "Not provided"}
              </p>
              <p style={{ color: currentTheme.text }}>
                <strong>Role:</strong> {user?.role || "Not provided"}
              </p>
              <p style={{ color: currentTheme.text }}>
                <strong>User ID:</strong> {user?.id || "Not provided"}
              </p>
              <p style={{ color: currentTheme.text }}>
                <strong>Account Created:</strong>{" "}
                {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : "Not available"}
              </p>
            </div>
          </div>

          {/* Action Buttons - Only show for own profile */}
          {isOwnProfile && (
            <div
              className="flex flex-col sm:flex-row gap-4 pt-6"
              style={{ borderTop: `1px solid ${currentTheme.border}` }}
            >
              <button
                onClick={handleEditClick}
                className="flex-1 bg-gradient-to-r from-teal-600 to-blue-500 text-white py-3 px-6 rounded-lg hover:from-teal-700 hover:to-blue-600 transition-all duration-300 font-semibold flex items-center justify-center shadow-lg"
              >
                <FaEdit className="w-5 h-5 mr-2" />
                Edit Profile
              </button>

              <button
                onClick={handleVerification}
                disabled={verifyOrganizationMutation.isPending}
                className="flex-1 bg-gradient-to-r from-orange-500 to-pink-500 text-white py-3 px-6 rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all duration-300 font-semibold flex items-center justify-center shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifyOrganizationMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Submitting Request...
                  </>
                ) : (
                  <>
                    <FaInfoCircle className="w-5 h-5 mr-2" />
                    Request Verification
                  </>
                )}
              </button>
              <button
                onClick={() => setIsPasswordModalOpen(true)}
                className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-6 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 font-semibold flex items-center justify-center shadow-lg"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Change Password
              </button>
            </div>

          )}

          <ChangePasswordModal
            isOpen={isPasswordModalOpen}
            onClose={() => setIsPasswordModalOpen(false)}
            changePasswordService={(oldPass, newPass, confirmPass) => changePasswordMutation.mutateAsync({ old_password: oldPass, new_password: newPass, confirm_password: confirmPass })}
          />
        </div>
      )}
    </div>
  );
};

export default InfluencerProfile;